import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Form,
  Input,
  Button,
  Select,
  Switch,
  Card,
  Row,
  Col,
  message,
  Typography,
  Space
} from 'antd'
import {
  LockOutlined,
  MailOutlined,
  UserOutlined,
  PhoneOutlined
} from '@ant-design/icons'
import { User, UserFormData, UserUpdate } from '../../types/users'
import userService from '../../services/userService'

const { Title } = Typography
const { Option } = Select

interface UserFormProps {
  initialData?: User
  isEdit?: boolean
}

const UserForm: React.FC<UserFormProps> = ({ initialData, isEdit = false }) => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)

  // Thiết lập dữ liệu ban đầu cho form khi ở chế độ chỉnh sửa
  useEffect(() => {
    if (isEdit && initialData) {
      form.setFieldsValue({
        fullName: initialData.fullName,
        email: initialData.email,
        phone: initialData.phone,
        role: initialData.role,
        isActive: initialData.isActive
      })
    }
  }, [form, initialData, isEdit])

  // Xử lý khi submit form
  const onFinish = async (values: any) => {
    const formData: UserFormData = {
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      role: values.role,
      password: values.password
    }

    setLoading(true)
    try {
      if (isEdit && initialData) {
        const userUpdate: UserUpdate = {
          id: initialData._id,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          isActive: values.isActive
        }
        await userService.updateUser(userUpdate)
        message.success('Cập nhật người dùng thành công!')
      } else {
        await userService.createUser(formData)
        message.success('Tạo người dùng mới thành công!')
      }
      navigate('/admin/users')
    } catch (error: any) {
      console.error('Lỗi khi lưu người dùng:', error)
      message.error(error.message || 'Có lỗi xảy ra khi lưu người dùng')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className='w-full'>
      <Title level={3} className='mb-6'>
        {isEdit ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}
      </Title>

      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        autoComplete='off'
        initialValues={{
          isActive: true,
          role: 'admin'
        }}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name='fullName'
              label='Họ tên'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập họ tên'
                }
              ]}
            >
              <Input
                prefix={<UserOutlined className='site-form-item-icon' />}
                placeholder='Nhập họ tên'
              />
            </Form.Item>

            <Form.Item
              name='email'
              label='Email'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập email'
                },
                {
                  type: 'email',
                  message: 'Email không hợp lệ'
                }
              ]}
            >
              <Input
                prefix={<MailOutlined className='site-form-item-icon' />}
                placeholder='Nhập email'
                disabled={isEdit}
              />
            </Form.Item>

            <Form.Item
              name='password'
              label={isEdit ? 'Mật khẩu (để trống nếu không đổi)' : 'Mật khẩu'}
              rules={[
                {
                  required: !isEdit,
                  message: 'Vui lòng nhập mật khẩu'
                },
                {
                  min: 6,
                  message: 'Mật khẩu phải có ít nhất 6 ký tự'
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className='site-form-item-icon' />}
                placeholder='Nhập mật khẩu'
                disabled={isEdit}
              />
            </Form.Item>

            <Form.Item name='phone' label='Số điện thoại'>
              <Input
                prefix={<PhoneOutlined className='site-form-item-icon' />}
                placeholder='Nhập số điện thoại'
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name='role'
              label='Vai trò'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn vai trò'
                }
              ]}
            >
              <Select placeholder='Chọn vai trò'>
                <Option value='admin'>Quản trị viên</Option>
                <Option value='user'>Người dùng</Option>
              </Select>
            </Form.Item>

            <Form.Item name='isActive' label='Trạng thái' valuePropName='checked'>
              <Switch checkedChildren='Hoạt động' unCheckedChildren='Vô hiệu' />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className='mt-4'>
          <Space>
            <Button onClick={() => navigate('/admin/users')}>Hủy</Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              className='bg-green-600 border-green-600 hover:bg-green-700'
            >
              {isEdit ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}

export default UserForm
